import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import { FY_ALL, useFYStore } from "@/modules/financialYear";

// ---- Types ----

export interface StepCompletionRow {
  id: string;
  completedById: string | null;
  completedByName: string | null;
  completedAt: string | null;
  valueText: string | null;
  valueNumber: number | null;
  supportingText: string | null;
  attachmentId: string | null;
  attachmentFilename: string | null;
  attachmentContentType: string | null;
  caption: string | null;
  approvalStatus: "pending" | "approved" | "rejected" | null;
  approvalResolvedByName: string | null;
  approvalResolvedAt: string | null;
  approvalResolutionNote: string | null;
  rejectedToStepId: string | null;
  supersededAt: string | null;
  supersededReason: string | null;
  isClientDocumentIntent?: boolean;
  publishedClientDocumentId?: string | null;
}

export interface WorkUnitStepRow {
  id: string;
  name: string;
  stepType: string;
  order: number;
  weight: number;
  config: Record<string, unknown>;
  state: "locked" | "pending" | "awaiting_approval" | "done" | "rejected";
  completion: StepCompletionRow | null;
}

export interface WorkUnitWorkflowRow {
  id: string;
  name: string;
  order: number;
  state: "past" | "current" | "future";
  departmentId: string | null;
  departmentName: string | null;
  departmentHeadName: string | null;
  sopAttachmentId: string | null;
  assigneeId: string | null;
  assigneeName: string | null;
  steps: WorkUnitStepRow[];
}

export interface WorkUnitRow {
  id: string;
  ctrlNo: string;
  projectId: string;
  projectName: string | null;
  projectCtrlPrefix: string | null;
  projectIsLegacy: boolean;
  clientId: string;
  clientName: string | null;
  financialYear: string;
  description: string;
  dueAt: string | null;
  currentWorkflowId: string;
  currentWorkflowName: string | null;
  currentWorkflowOrder: number | null;
  assignedToMemberId: string | null;
  assignedToMemberName: string | null;
  currentWorkflowAssigneeId: string | null;
  currentWorkflowAssigneeName: string | null;
  currentDeptHeadName: string | null;
  status: "active" | "inactive";
  lifecycleState: "active" | "completed" | "inactive";
  sendStartIntimation: boolean;
  intimationStatus: "" | "pending" | "sending" | "sent" | "failed" | "skipped";
  intimationTo: string;
  intimationSentAt: string | null;
  intimationError: string;
  createdAt: string | null;
  updatedAt: string | null;
  createdById: string | null;
  stepsCompleted: number;
  totalSteps: number;
  pendingDeactivationRequest: PendingDeactivationRequest | null;
}

export interface PendingDeactivationRequest {
  id: string;
  requestedById: string | null;
  requestedByName: string | null;
  remark: string;
  reasonId: string | null;
  reasonName: string | null;
  proofAttachmentId: string | null;
  proofAttachmentFilename: string | null;
  proofAttachmentContentType: string | null;
  duplicateWorkUnitId: string | null;
  duplicateWorkUnitCtrlNo: string | null;
  routedToDeptId: string | null;
  routedToDeptName: string | null;
  routedToDeptHeadIds: string[];
  createdAt: string | null;
}

export interface DeactivationRequestRow {
  id: string;
  workUnitId: string;
  workUnitCtrlNo: string | null;
  projectId: string | null;
  projectName: string | null;
  requestedById: string | null;
  requestedByName: string | null;
  remark: string;
  reasonId: string | null;
  reasonName: string | null;
  proofAttachmentId: string | null;
  proofAttachmentFilename: string | null;
  proofAttachmentContentType: string | null;
  duplicateWorkUnitId: string | null;
  duplicateWorkUnitCtrlNo: string | null;
  requestStatus: "pending" | "approved" | "rejected" | "withdrawn";
  routedToDeptId: string | null;
  routedToDeptName: string | null;
  resolvedById: string | null;
  resolvedByName: string | null;
  resolvedAt: string | null;
  resolutionNote: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface DeactivationReasonRow {
  id: string;
  name: string;
  description: string;
  requiresProofAttachment: boolean;
  requiresDuplicateWorkUnit: boolean;
  sortOrder: number;
  status: "active" | "inactive";
  createdAt: string | null;
  updatedAt: string | null;
}

export interface DeactivationRequestListResponse {
  results: DeactivationRequestRow[];
  nextCursor: string | null;
  prevCursor: string | null;
}

export interface WorkUnitRequestRow {
  id: string;
  projectId: string;
  projectName: string | null;
  clientId: string;
  clientName: string | null;
  description: string;
  dueAt: string | null;
  requestedById: string;
  requestedByName: string | null;
  requestStatus: "pending" | "approved" | "rejected";
  resolvedById: string | null;
  resolvedByName: string | null;
  resolvedAt: string | null;
  resolutionNote: string;
  createdWorkUnitId: string | null;
  createdAt: string | null;
}

export interface WorkUnitRequestListResponse {
  results: WorkUnitRequestRow[];
  nextCursor: string | null;
  prevCursor: string | null;
}

export interface CtrlPreview {
  ctrlPrefix: string;
  financialYear: string;
  preview: string;
}

export type WuHistoryStepAction =
  | "submitted"
  | "submitted_for_approval"
  | "approved"
  | "rejected";

export interface WuHistoryStepEvent {
  kind: "step";
  id: string;
  timestamp: string | null;
  submittedAt: string | null;
  submittedById: string | null;
  submittedByName: string | null;
  resolvedAt: string | null;
  resolvedById: string | null;
  resolvedByName: string | null;
  stepId: string | null;
  stepName: string | null;
  stepType: WorkUnitStepRow["stepType"] | null;
  workflowId: string | null;
  workflowName: string | null;
  workflowOrder: number | null;
  action: WuHistoryStepAction;
  approvalStatus: "approved" | "rejected" | "pending" | null;
  valueText: string | null;
  valueNumber: number | null;
  supportingText: string | null;
  attachmentId: string | null;
  attachmentFilename: string | null;
  attachmentContentType: string | null;
  approvalResolutionNote: string | null;
  rejectedToStepId: string | null;
  rejectedToStepName: string | null;
  rejectedToWorkflowName: string | null;
  supersededAt: string | null;
  supersededReason: string | null;
  supersededTargetStepId: string | null;
  supersededTargetStepName: string | null;
  supersededTargetWorkflowName: string | null;
  supersededRejecterNote: string | null;
  supersededRejecterName: string | null;
  proposedRollbackToStepId: string | null;
  proposedRollbackByName: string | null;
  proposedRollbackAt: string | null;
  proposedRollbackReason: string | null;
}

export interface WuHistoryAdvanceEvent {
  kind: "advance";
  id: string;
  timestamp: string | null;
  actorName: string | null;
  fromWorkflowName: string | null;
  fromWorkflowOrder: number | null;
  toWorkflowName: string | null;
  toWorkflowOrder: number | null;
}

export type WuHistoryEvent = WuHistoryStepEvent | WuHistoryAdvanceEvent;

export interface WorkUnitDetail extends WorkUnitRow {
  flowProgress: WorkUnitWorkflowRow[];
  history?: WuHistoryEvent[];
}

export interface WorkUnitListResponse {
  results: WorkUnitRow[];
  nextCursor: string | null;
  prevCursor: string | null;
}

// ---- Queries ----

export type WorkUnitLifecycleFilter =
  | "active"
  | "completed"
  | "all"
  | "";

export type WorkUnitScope = "all" | "mine";
export type WorkUnitLegacyFilter = "" | "all" | "legacy" | "new";

export interface WorkUnitListParams {
  search?: string;
  status?: "active" | "inactive" | "all" | "";
  /** Lifecycle tab — server defaults to `active` when omitted. */
  lifecycle?: WorkUnitLifecycleFilter;
  projectId?: string;
  clientId?: string;
  departmentId?: string;
  financialYear?: string;
  scope?: WorkUnitScope;
  legacy?: WorkUnitLegacyFilter;
  pageSize?: number;
}

export function useWorkUnits(
  params: WorkUnitListParams = {},
  options: { enabled?: boolean } = {},
) {
  const selectedFy = useFYStore((s) => s.selected);
  const effectiveFy =
    params.financialYear !== undefined
      ? params.financialYear
      : selectedFy === FY_ALL
        ? ""
        : selectedFy;
  return useQuery<WorkUnitListResponse>({
    queryKey: ["work-units", "list", { ...params, fy: effectiveFy || "ALL" }],
    enabled: options.enabled ?? true,
    queryFn: () =>
      api
        .get<WorkUnitListResponse>("/work-units", {
          params: {
            search: params.search || undefined,
            status: params.status ? params.status : undefined,
            lifecycle: params.lifecycle ? params.lifecycle : undefined,
            projectId: params.projectId || undefined,
            clientId: params.clientId || undefined,
            department: params.departmentId || undefined,
            financialYear: effectiveFy || undefined,
            scope: params.scope && params.scope !== "all" ? params.scope : undefined,
            legacy:
              params.legacy && params.legacy !== "all"
                ? params.legacy
                : undefined,
            pageSize: params.pageSize || undefined,
          },
        })
        .then((r) => r.data),
  });
}

export function useWorkUnit(id: string) {
  return useQuery<WorkUnitDetail>({
    queryKey: ["work-units", "detail", id],
    queryFn: () => api.get<WorkUnitDetail>(`/work-units/${id}`).then((r) => r.data),
    enabled: !!id,
  });
}

// ---- Mutations ----

export interface WorkUnitCreateInput {
  projectId: string;
  clientId: string;
  description?: string;
  dueAt?: string | null;
  /** null → inherit project default; true/false → per-WU override. */
  sendStartIntimation?: boolean | null;
}

export function useCreateWorkUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: WorkUnitCreateInput) =>
      api
        .post<WorkUnitRow>("/work-units", {
          project_id: input.projectId,
          client_id: input.clientId,
          description: input.description ?? "",
          due_at: input.dueAt ?? null,
          send_start_intimation: input.sendStartIntimation ?? null,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["work-units"] }),
  });
}

export function useResendIntimation(wuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<WorkUnitRow>(`/work-units/${wuId}/resend-intimation`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["work-units"] }),
  });
}

export function useUpdateWorkUnit(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { description?: string; dueAt?: string | null }) =>
      api
        .patch<WorkUnitRow>(`/work-units/${id}`, {
          description: input.description,
          due_at: input.dueAt,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["work-units"] }),
  });
}

export function useCtrlNoPreview(projectId: string) {
  return useQuery<CtrlPreview>({
    queryKey: ["work-units", "ctrl-preview", projectId],
    queryFn: () =>
      api.get<CtrlPreview>(`/projects/${projectId}/ctrl-no-preview`).then((r) => r.data),
    enabled: !!projectId,
  });
}

export interface WorkUnitRequestCreateInput {
  projectId: string;
  clientId: string;
  description?: string;
  dueAt?: string | null;
}

export function useWorkUnitRequests(
  params: { projectId?: string; status?: string; statuses?: string } = {},
  opts: { enabled?: boolean } = {},
) {
  return useQuery<WorkUnitRequestListResponse>({
    queryKey: ["work-unit-requests", "list", params],
    queryFn: () =>
      api
        .get<WorkUnitRequestListResponse>("/work-unit-requests", {
          params: {
            projectId: params.projectId || undefined,
            status: params.status || undefined,
            statuses: params.statuses || undefined,
          },
        })
        .then((r) => r.data),
    enabled: opts.enabled ?? true,
    staleTime: 15_000,
  });
}

export function useCreateWorkUnitRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: WorkUnitRequestCreateInput) =>
      api
        .post<WorkUnitRequestRow>("/work-unit-requests", {
          project_id: input.projectId,
          client_id: input.clientId,
          description: input.description ?? "",
          due_at: input.dueAt ?? null,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["work-unit-requests"] }),
  });
}

export function useResolveWorkUnitRequest(reqId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { decision: "approved" | "rejected"; note?: string }) =>
      api
        .post<WorkUnitRequestRow>(`/work-unit-requests/${reqId}/resolve`, input)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work-unit-requests"] });
      qc.invalidateQueries({ queryKey: ["work-units"] });
    },
  });
}

export function useDeactivateWorkUnit(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      api.post<WorkUnitRow>(`/work-units/${id}/deactivate`, { reason }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work-units"] });
      qc.invalidateQueries({ queryKey: ["work-unit-requests"] });
      qc.invalidateQueries({ queryKey: ["wu-deactivation-requests"] });
    },
  });
}

export interface DuplicateClientCheckItem {
  id: string;
  ctrlNo: string;
  lifecycleState: "active" | "completed" | "inactive";
  currentWorkflowName: string | null;
  assigneeName: string | null;
  createdByName: string | null;
  financialYear: string;
  createdAt: string | null;
}

export interface DuplicateClientCheckResponse {
  projectId: string;
  clientId: string;
  count: number;
  items: DuplicateClientCheckItem[];
}

export function useDuplicateClientCheck(projectId: string, clientId: string) {
  return useQuery<DuplicateClientCheckResponse>({
    queryKey: ["duplicate-client-check", projectId, clientId],
    queryFn: () =>
      api
        .get<DuplicateClientCheckResponse>(
          `/projects/${projectId}/duplicate-client-check`,
          { params: { clientId } },
        )
        .then((r) => r.data),
    enabled: !!projectId && !!clientId,
    staleTime: 10_000,
  });
}

export function useRequestWorkUnitDeactivation(wuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      remark: string;
      reasonId: string;
      proofAttachmentId?: string | null;
      duplicateWorkUnitId?: string | null;
      duplicateWorkUnitCtrlNo?: string | null;
    }) =>
      api
        .post<DeactivationRequestRow>(
          `/work-units/${wuId}/deactivation-requests`,
          {
            remark: input.remark,
            reason_id: input.reasonId,
            proof_attachment_id: input.proofAttachmentId ?? null,
            duplicate_work_unit_id: input.duplicateWorkUnitId ?? null,
            duplicate_work_unit_ctrl_no: input.duplicateWorkUnitCtrlNo ?? "",
          },
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work-units"] });
      qc.invalidateQueries({ queryKey: ["wu-deactivation-requests"] });
    },
  });
}

// ---- Deactivation Reasons (master data; SA CRUD) ----

export function useDeactivationReasons(
  params: { status?: "active" | "inactive" | "all" } = {},
) {
  return useQuery<DeactivationReasonRow[]>({
    queryKey: ["deactivation-reasons", "list", params.status ?? "active"],
    queryFn: () =>
      api
        .get<DeactivationReasonRow[]>("/deactivation-reasons", {
          params: { status: params.status ?? "active" },
        })
        .then((r) => r.data),
  });
}

export function useCreateDeactivationReason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      description?: string;
      requiresProofAttachment?: boolean;
      requiresDuplicateWorkUnit?: boolean;
      sortOrder?: number;
    }) =>
      api
        .post<DeactivationReasonRow>("/deactivation-reasons", {
          name: input.name,
          description: input.description ?? "",
          requires_proof_attachment: input.requiresProofAttachment ?? false,
          requires_duplicate_work_unit: input.requiresDuplicateWorkUnit ?? false,
          sort_order: input.sortOrder ?? 0,
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deactivation-reasons"] }),
  });
}

export function useUpdateDeactivationReason(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name?: string;
      description?: string;
      requiresProofAttachment?: boolean;
      requiresDuplicateWorkUnit?: boolean;
      sortOrder?: number;
    }) => {
      const body: Record<string, unknown> = {};
      if (input.name !== undefined) body.name = input.name;
      if (input.description !== undefined) body.description = input.description;
      if (input.requiresProofAttachment !== undefined)
        body.requires_proof_attachment = input.requiresProofAttachment;
      if (input.requiresDuplicateWorkUnit !== undefined)
        body.requires_duplicate_work_unit = input.requiresDuplicateWorkUnit;
      if (input.sortOrder !== undefined) body.sort_order = input.sortOrder;
      return api
        .patch<DeactivationReasonRow>(`/deactivation-reasons/${id}`, body)
        .then((r) => r.data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deactivation-reasons"] }),
  });
}

export function useDeactivateDeactivationReason(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api
        .post<DeactivationReasonRow>(`/deactivation-reasons/${id}/deactivate`)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deactivation-reasons"] }),
  });
}

export function useActivateDeactivationReason(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api
        .post<DeactivationReasonRow>(`/deactivation-reasons/${id}/activate`)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deactivation-reasons"] }),
  });
}

export function useDeactivationRequests(
  params: { status?: "pending" | "approved" | "rejected" | "withdrawn" | "all" | string } = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery<DeactivationRequestListResponse>({
    queryKey: ["wu-deactivation-requests", "list", params],
    queryFn: () =>
      api
        .get<DeactivationRequestListResponse>("/work-unit-deactivation-requests", {
          params: { status: params.status ?? "pending" },
        })
        .then((r) => r.data),
    enabled: options.enabled ?? true,
  });
}

export function useResolveDeactivationRequest(reqId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      outcome: "approved" | "rejected";
      resolutionNote?: string;
    }) =>
      api
        .post<DeactivationRequestRow>(
          `/work-unit-deactivation-requests/${reqId}/resolve`,
          {
            outcome: input.outcome,
            resolution_note: input.resolutionNote ?? "",
          },
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["wu-deactivation-requests"] });
      qc.invalidateQueries({ queryKey: ["work-units"] });
    },
  });
}

export function useWithdrawDeactivationRequest(reqId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api
        .post<DeactivationRequestRow>(
          `/work-unit-deactivation-requests/${reqId}/withdraw`,
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["wu-deactivation-requests"] });
      qc.invalidateQueries({ queryKey: ["work-units"] });
    },
  });
}

export function useActivateWorkUnit(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<WorkUnitRow>(`/work-units/${id}/activate`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work-units"] });
      qc.invalidateQueries({ queryKey: ["work-unit-requests"] });
    },
  });
}

export interface UserPendingWorkUnitRow {
  workUnitId: string;
  ctrlNo: string;
  projectId: string;
  projectName: string | null;
  projectIsLegacy: boolean;
  workflowId: string;
  workflowName: string;
  workflowState: "past" | "current" | "future";
  currentStepName: string | null;
  assignedAt: string | null;
  ageDays: number | null;
  clientId: string | null;
  clientName: string | null;
  departmentId: string | null;
  departmentName: string | null;
  lastActionAt: string | null;
  lastActionByName: string | null;
}

export function useUserPendingWorkUnits(
  userId: string | undefined,
  options: { enabled?: boolean; financialYear?: string } = {},
) {
  const selectedFy = useFYStore((s) => s.selected);
  const effectiveFy =
    options.financialYear !== undefined
      ? options.financialYear
      : selectedFy === FY_ALL
        ? ""
        : selectedFy;
  return useQuery<UserPendingWorkUnitRow[]>({
    queryKey: ["users", userId ?? "", "pending-workunits", effectiveFy || "ALL"],
    queryFn: () =>
      api
        .get<UserPendingWorkUnitRow[]>(`/users/${userId}/pending-workunits`, {
          params: { financialYear: effectiveFy || undefined },
        })
        .then((r) => r.data),
    enabled: !!userId && (options.enabled ?? true),
  });
}

export function useAssignWorkflow(wuId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { wuId?: string; projectWorkflowId: string; userId: string }) => {
      const id = input.wuId || wuId;
      if (!id) throw new Error("wuId required");
      return api
        .post<WorkUnitDetail>(`/work-units/${id}/assign-workflow`, {
          project_workflow_id: input.projectWorkflowId,
          user_id: input.userId,
        })
        .then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work-units"] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useSubmitStep(wuId: string, stepId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      value_text?: string | null;
      value_number?: number | null;
      supporting_text?: string | null;
      attachment_id?: string | null;
      caption?: string | null;
    }) =>
      api
        .post<StepCompletionRow>(`/work-units/${wuId}/steps/${stepId}/submit`, input)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work-units"] });
      qc.invalidateQueries({ queryKey: ["me", "session-state"] });
    },
  });
}

export function useSubmitForApproval(wuId: string, stepId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      supporting_text?: string | null;
      attachment_id: string;
      caption?: string | null;
      is_client_document_intent?: boolean;
    }) =>
      api
        .post<StepCompletionRow>(`/work-units/${wuId}/steps/${stepId}/submit-for-approval`, {
          supporting_text: input.supporting_text ?? null,
          attachment_id: input.attachment_id,
          caption: input.caption ?? null,
          is_client_document_intent: input.is_client_document_intent ?? false,
        })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work-units"] });
      qc.invalidateQueries({ queryKey: ["me", "session-state"] });
    },
  });
}

// ---- Email step ----

export interface EmailToken {
  token: string;
  label: string;
}

export interface EmailRecipientSource {
  key: string;
  label: string;
}

export interface EmailStepPreview {
  subject: string;
  bodyHtml: string;
  bodyInnerHtml: string;
  to: string[];
  cc: string[];
  unknownTokens: string[];
  tokens: EmailToken[];
  recipientSources: EmailRecipientSource[];
}

export interface EmailLogRow {
  id: string;
  status: "sent" | "failed" | "forced";
  at: string | null;
  actorName: string | null;
  subject: string | null;
  to: string[];
  cc: string[];
  error: string | null;
  reason: string | null;
  override: boolean;
  bodyHtml: string | null;
}

/** Render the acknowledgment preview (optionally with at-send overrides). */
export function useEmailStepPreview(wuId: string, stepId: string) {
  return useMutation({
    mutationFn: (input: { subject?: string | null; bodyMarkdown?: string | null }) =>
      api
        .post<EmailStepPreview>(
          `/work-units/${wuId}/steps/${stepId}/email-preview`,
          { subject: input.subject ?? null, bodyMarkdown: input.bodyMarkdown ?? null },
        )
        .then((r) => r.data),
  });
}

/** Send the acknowledgment email AND complete the step (send == complete). */
export function useSendEmailStep(wuId: string, stepId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      subject?: string | null;
      bodyMarkdown?: string | null;
      to: string[];
      cc: string[];
    }) =>
      api
        .post<StepCompletionRow>(`/work-units/${wuId}/steps/${stepId}/send-email`, {
          subject: input.subject ?? null,
          bodyMarkdown: input.bodyMarkdown ?? null,
          to: input.to,
          cc: input.cc,
        })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work-units"] });
      qc.invalidateQueries({ queryKey: ["me", "session-state"] });
    },
  });
}

/** SA/Ops escape hatch — complete an email step without sending. */
export function useForceCompleteEmailStep(wuId: string, stepId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { reason?: string }) =>
      api
        .post<StepCompletionRow>(
          `/work-units/${wuId}/steps/${stepId}/force-complete-email`,
          { reason: input.reason ?? "" },
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work-units"] });
      qc.invalidateQueries({ queryKey: ["me", "session-state"] });
    },
  });
}

export function useEmailStepLog(wuId: string, stepId: string, enabled: boolean) {
  return useQuery<EmailLogRow[]>({
    queryKey: ["work-units", "email-log", wuId, stepId],
    queryFn: () =>
      api
        .get<EmailLogRow[]>(`/work-units/${wuId}/steps/${stepId}/email-log`)
        .then((r) => r.data),
    enabled: enabled && !!wuId && !!stepId,
  });
}

export function usePublishStepDocToClient(wuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scId: string) =>
      api
        .post(`/work-units/${wuId}/step-completions/${scId}/publish-to-client`, {})
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work-units"] });
      qc.invalidateQueries({ queryKey: ["client-docs"] });
    },
  });
}

export function useResolveApproval(wuId: string, stepId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      decision: "approved" | "rejected";
      note?: string;
      rejected_to_step_id?: string | null;
      publish_to_client_portal?: boolean | null;
    }) =>
      api
        .post<StepCompletionRow>(`/work-units/${wuId}/steps/${stepId}/resolve-approval`, input)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work-units"] });
      qc.invalidateQueries({ queryKey: ["me", "session-state"] });
    },
  });
}

export function useAssignWorkUnitStep(wuId: string, stepId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api
        .post<{ id: string; assignedToMemberId: string }>(
          `/work-units/${wuId}/steps/${stepId}/assign`,
          { user_id: userId },
        )
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["work-units"] }),
  });
}

export function useRevokeStepAssignment(wuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) =>
      api
        .post(`/work-units/${wuId}/step-assignments/${assignmentId}/revoke`, {})
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["work-units"] }),
  });
}
